import { registerOverlay } from 'klinecharts'
import { type OverlayFigure } from './overlayUtils'
import { NO_DEFAULT_FIGURES, type OverlayTemplateParam, registerOnce } from './registrationUtils'

/**
 * Bar highlight — paints the candle under the cursor yellow while the user holds
 * Shift on a daily chart, matching the yellow vertical crosshair. It marks the
 * day a Shift+click would drill into (see ChartManager.shift* handlers and the
 * `intradayDrill:requested` event).
 *
 * The fill traces the candle SHAPE — a thin wick (high→low) plus a wider body
 * (open→close) — rather than a full bounding box, so the empty space beside the
 * wick stays clear. No background band, no border.
 *
 * Created with FOUR points (high, low, open, close) so the fill can bound the
 * wick and body in price space. It follows the cursor by overriding points on
 * each crosshair change. The overlay is EPHEMERAL — ChartManager removes it on
 * Shift up / pointer leave / period change and skips persistence.
 *
 * Figure construction is pure (unit-tested); `registerBarHighlightOverlay`
 * wires it into a klinecharts overlay template.
 */

// Concrete color (overlays paint to canvas, so they can't read CSS vars).
// Same yellow as the history-mode crosshair; high alpha so the bar reads yellow.
export const HIGHLIGHT_FILL = 'rgba(250, 204, 21, 0.85)'

export const BAR_HIGHLIGHT_NAME = 'barHighlight'

type Figure = OverlayFigure

export interface BarHighlightInput {
  /** Bar center x (pixels). */
  x: number
  /** Candle body width for the body fill. */
  candleWidth: number
  /** Wick fill width (thin). */
  wickWidth: number
  /** High / low / open / close pixel coordinates. */
  highY: number
  lowY: number
  openY: number
  closeY: number
}

function fillRect(x: number, y: number, width: number, height: number): Figure {
  return {
    type: 'rect',
    ignoreEvent: true,
    attrs: { x, y, width, height },
    styles: { style: 'fill', color: HIGHLIGHT_FILL },
  }
}

/** Yellow fill tracing the candle: thin wick (high→low) + wider body (open→close). */
export function buildBarHighlightFigures(input: BarHighlightInput): Figure[] {
  const { x, candleWidth, wickWidth, highY, lowY, openY, closeY } = input
  const nums = [x, candleWidth, wickWidth, highY, lowY, openY, closeY]
  if (!nums.every((n) => Number.isFinite(n))) return []

  const candle = Math.max(candleWidth, 3)
  const wick = Math.max(wickWidth, 1)

  // Smaller y = higher price, so "top" is the min y.
  const top = Math.min(highY, lowY)
  const bottom = Math.max(highY, lowY)
  const bodyTop = Math.min(openY, closeY)
  const bodyBottom = Math.max(openY, closeY)

  const figures: Figure[] = []

  // Wick, drawn as the two segments above and below the body so it doesn't
  // double up under the body fill.
  const upperWick = bodyTop - top
  if (upperWick > 0) figures.push(fillRect(x - wick / 2, top, wick, upperWick))
  const lowerWick = bottom - bodyBottom
  if (lowerWick > 0) figures.push(fillRect(x - wick / 2, bodyBottom, wick, lowerWick))

  // Body (open→close), floored to 1px so a doji still shows.
  figures.push(fillRect(x - candle / 2, bodyTop, candle, Math.max(bodyBottom - bodyTop, 1)))

  return figures
}

/** Register the `barHighlight` overlay template with klinecharts (once). */
export const registerBarHighlightOverlay = registerOnce(() => {
  registerOverlay({
    name: BAR_HIGHLIGHT_NAME,
    totalStep: 5, // four points (high, low, open, close) + 1
    lock: true,
    ...NO_DEFAULT_FIGURES,
    createPointFigures: ({
      coordinates,
      barSpace,
    }: {
      coordinates: Array<{ x: number; y: number }>
      barSpace: { gapBar?: number; bar?: number } | undefined
    }) => {
      const [high, low, open, close] = coordinates
      if (!high || !low || !open || !close) return []
      const candleWidth = barSpace?.gapBar ?? barSpace?.bar ?? 6
      return buildBarHighlightFigures({
        x: high.x,
        candleWidth,
        wickWidth: Math.max(2, candleWidth * 0.18),
        highY: high.y,
        lowY: low.y,
        openY: open.y,
        closeY: close.y,
      })
    },
  } as OverlayTemplateParam)
})
